import { type SyntheticEvent, useEffect, useState } from 'react'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import FormHelperText from '@mui/material/FormHelperText'
import IconButton from '@mui/material/IconButton'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import type { Customer, NewExternalUserCustomerAccess } from 'common-billing-server/types'
import { Role } from 'common-billing-server/types'
import { externalRoles } from 'common-billing-server/role'

import { useBillingApi } from '../../api/billing/billing-context'
import { commonTextFieldProps } from '../../theme/common-styles'
import { email, notEmpty } from '../../validators'
import { formatExternalRole } from '../customer/externalRoleDisplay'
import { ValidatedInput } from '../common/InputWithValidation'
import Wrapper from '../common/Wrapper'
import { useFailedOperationBanner } from '../common/hooks/hook-failed-operation'
import { useStatefulNavigate } from '../common/hooks/hook-stateful-navigate'

const emptyCustomerAccess = (): NewExternalUserCustomerAccess => ({ customerId: '', role: Role.external })

export const CreateExternalUserPage = () => {
    const billingApi = useBillingApi()
    const { navigateBack } = useStatefulNavigate()
    const { addFailedOperation, removeFailedOperation, errorBanner } = useFailedOperationBanner()
    const [customers, setCustomers] = useState<Customer[]>([])
    const [username, setUsername] = useState('')
    const [customerAccess, setCustomerAccess] = useState<NewExternalUserCustomerAccess[]>([emptyCustomerAccess()])
    const [isLoading, setIsLoading] = useState(false)
    const [hasSubmittedForm, setHasSubmittedForm] = useState(false)
    const [validationErrors, setValidationErrors] = useState({
        username: undefined as string | undefined,
    })

    useEffect(() => {
        void billingApi
            .listCustomers({ limit: 0 })
            .then(({ items }) => setCustomers(items))
            .catch(() => addFailedOperation({ id: 'load-customers', message: 'Failed fetching customers' }))
    }, [addFailedOperation, billingApi])

    const selectedCustomerIds = customerAccess.map(({ customerId }) => customerId).filter(Boolean)
    const hasDuplicateCustomers = new Set(selectedCustomerIds).size !== selectedCustomerIds.length
    const hasMissingCustomer = customerAccess.some(({ customerId }) => !customerId)
    const formContainsInvalidInput =
        Object.values(validationErrors).some(Boolean) || hasMissingCustomer || hasDuplicateCustomers

    const updateAccess = (index: number, access: NewExternalUserCustomerAccess) => {
        setCustomerAccess((current) => current.map((entry, entryIndex) => (entryIndex === index ? access : entry)))
    }

    const createUser = async (event: SyntheticEvent) => {
        event.preventDefault()
        setHasSubmittedForm(true)
        if (formContainsInvalidInput) return

        const operationId = 'create-external-user'
        removeFailedOperation(operationId)
        setIsLoading(true)
        try {
            await billingApi.createExternalUser({ username, customerAccess })
            navigateBack({ replace: true })
        } catch (error) {
            addFailedOperation({ id: operationId, message: 'Failed creating external user', error })
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <Wrapper>
            <Paper>
                <Box
                    component="form"
                    onSubmit={(event) => void createUser(event)}
                    sx={{ p: 2.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                >
                    <ValidatedInput
                        {...commonTextFieldProps()}
                        type="text"
                        label="Email"
                        autoFocus
                        disabled={isLoading}
                        validators={[notEmpty, email]}
                        value={username}
                        onChange={setUsername}
                        onValidationError={(error) =>
                            setValidationErrors((current) => ({ ...current, username: error }))
                        }
                        showErrorDespiteUntouched={hasSubmittedForm}
                    />
                    <FormHelperText sx={{ alignSelf: 'flex-start' }}>
                        The user receives an email with a link for setting their own password.
                    </FormHelperText>

                    <Box sx={{ width: '100%', mt: 1 }}>
                        {customerAccess.map((access, index) => (
                            <Box key={index} sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                                <TextField
                                    select
                                    fullWidth
                                    label="Customer"
                                    value={access.customerId}
                                    disabled={isLoading}
                                    error={hasSubmittedForm && !access.customerId}
                                    onChange={(event) =>
                                        updateAccess(index, { ...access, customerId: event.target.value })
                                    }
                                >
                                    {customers.map((customer) => (
                                        <MenuItem key={customer.id} value={customer.id}>
                                            {customer.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <TextField
                                    select
                                    fullWidth
                                    label="Role"
                                    value={access.role}
                                    disabled={isLoading}
                                    onChange={(event) =>
                                        updateAccess(index, { ...access, role: event.target.value as Role })
                                    }
                                >
                                    {externalRoles.map((role) => (
                                        <MenuItem key={role} value={role}>
                                            {formatExternalRole(role)}
                                        </MenuItem>
                                    ))}
                                </TextField>
                                <IconButton
                                    aria-label={`Remove customer ${index + 1}`}
                                    disabled={isLoading || customerAccess.length === 1}
                                    onClick={() =>
                                        setCustomerAccess((current) =>
                                            current.filter((_, entryIndex) => entryIndex !== index)
                                        )
                                    }
                                >
                                    <DeleteIcon />
                                </IconButton>
                            </Box>
                        ))}
                        {hasSubmittedForm && hasDuplicateCustomers ? (
                            <FormHelperText error>A customer can only be added once.</FormHelperText>
                        ) : null}
                        <Button
                            startIcon={<AddIcon />}
                            disabled={isLoading || customerAccess.length >= customers.length}
                            onClick={() => setCustomerAccess((current) => [...current, emptyCustomerAccess()])}
                        >
                            Add customer
                        </Button>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%', mt: 1 }}>
                        <Button
                            type="submit"
                            variant="contained"
                            disabled={formContainsInvalidInput || isLoading}
                            startIcon={isLoading ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                        >
                            Create User
                        </Button>
                    </Box>
                    {errorBanner}
                </Box>
            </Paper>
        </Wrapper>
    )
}
